import { Card, Typography, Box, IconButton, Button } from "@mui/material";
import { Add, Remove, HelpOutline, DeleteOutline } from "@mui/icons-material";
import { memo } from "react";
import { Area } from "../../context/scope-of-work.types";
import { useAreaActions } from "./hooks/useAreaActions";
import { colors, alphaVar } from "../../../../../../ui/colors";
import { cleanDescription } from "../../helpers/normalizedAreasFromBackend";

const COLORS = {
  orange: {
    base: colors.orange.base,
    100: "#FFE4CC",
  },
  gray: {
    100: "#F3F4F6",
    300: "#D1D5DB",
    400: "#9CA3AF",
    500: "#6B7280",
  },
  success: {
    600: "#10B981",
  },
};

interface AreaCardProps {
  area: Area;
  isOtherArea?: boolean;
}

const AreaCardBase = ({ area, isOtherArea = false }: AreaCardProps) => {
  const { changeQuantity, removeOtherArea } = useAreaActions();

  const quantity = Number((area as any)?.squareFootages?.[0]?.quantity) || 0;

  const activeTasksCount = Array.isArray((area as any).tasks)
    ? (area as any).tasks.filter((t: any) => t.active).length
    : 0;

  const handleDecrease = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (quantity === 0) return;
    changeQuantity(area.uid, -1);
  };

  const handleIncrease = (e: React.MouseEvent) => {
    e.stopPropagation();
    changeQuantity(area.uid, +1);
  };

  const handleRemoveOther = (e: React.MouseEvent) => {
    e.stopPropagation();
    removeOtherArea(area.uid);
  };

  return (
    <Card
      sx={{
        backgroundColor: "var(--fx-surface-base, #FFFFFF)",
        borderRadius: 2,
        border: `1px solid ${alphaVar(COLORS.gray[100], 0.8)}`,
        boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
        transition: "all 0.2s ease",
        overflow: "visible",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: `0 4px 8px ${alphaVar(COLORS.orange.base, 0.2)}`,
          borderColor: alphaVar(COLORS.orange.base, 0.3),
        },
        height: 200,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "space-between",
        position: "relative",
        p: 2,
      }}
    >
      {isOtherArea && (
        <IconButton
          onClick={handleRemoveOther}
          size="small"
          sx={{
            position: "absolute",
            top: 6,
            left: 6,
            zIndex: 999999999,
            color: COLORS.gray[400],
            "&:hover": { color: "#EF4444" },
          }}
        >
          <DeleteOutline fontSize="small" />
        </IconButton>
      )}

      <Box
        sx={{
          position: "absolute",
          top: 6,
          right: 6,
          zIndex: 99999999,
          "&:hover .help-overlay": {
            opacity: 1,
            pointerEvents: "auto",
            transform: "translateY(0)",
          },
        }}
      >
        <IconButton
          sx={{
            color: COLORS.gray[400],
            "&:hover": { color: COLORS.orange.base },
            zIndex: 999999999,
          }}
        >
          <HelpOutline fontSize="small" />
        </IconButton>

        <Box
          className="help-overlay"
          sx={{
            position: "absolute",
            top: 32,
            right: 0,
            width: 215,
            backgroundColor: "var(--fx-surface-base, #FFFFFF)",
            borderRadius: 2,
            p: 2,
            boxShadow: "0 4px 14px rgba(0,0,0,0.15)",
            border: "1px solid var(--fx-surface-border, #E2E8F0)",
            opacity: 0,
            transform: "translateY(-4px)",
            transition: "opacity 0.18s ease-out, transform 0.18s ease-out",
            pointerEvents: "none",
            zIndex: 999999999,
          }}
        >
          <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 1 }}>
            Area Information
          </Typography>

          <Typography
            sx={{
              fontSize: 12,
              color: COLORS.gray[500],
              lineHeight: 1.4,
            }}
          >
            {cleanDescription((area as any).description)}
          </Typography>
        </Box>
      </Box>

      <Box
        component="img"
        src={(area as any)?.imageUrl}
        alt={(area as any).name}
        sx={{
          width: 100,
          height: 80,
          objectFit: "contain",
        }}
      />

      <Typography
        variant="subtitle2"
        fontWeight={700}
        sx={{
          color: COLORS.gray[500],
          fontSize: 14,
          textAlign: "center",
        }}
      >
        {(area as any).name}
      </Typography>

      <Typography
        sx={{
          fontSize: 12,
          color: activeTasksCount > 0 ? COLORS.success[600] : COLORS.gray[400],
          mb: 1,
          fontWeight: 600,
        }}
      >
        {activeTasksCount} task{activeTasksCount !== 1 ? "s" : ""} attached
      </Typography>

      <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
        <Button
          onClick={handleDecrease}
          disabled={quantity === 0}
          sx={{
            minWidth: 36,
            height: 32,
            borderRadius: 1.5,
            color: quantity === 0 ? COLORS.gray[300] : COLORS.orange.base,
            border: `1px solid ${alphaVar(COLORS.orange.base, 0.3)}`,
            "&:hover": { backgroundColor: alphaVar(COLORS.orange[100], 0.1) },
          }}
        >
          <Remove fontSize="small" />
        </Button>

        <Typography
          sx={{
            width: 30,
            textAlign: "center",
            fontWeight: 600,
            color: COLORS.gray[500],
          }}
        >
          {quantity}
        </Typography>

        <Button
          onClick={handleIncrease}
          sx={{
            minWidth: 36,
            height: 32,
            borderRadius: 1.5,
            color: COLORS.orange.base,
            border: `1px solid ${alphaVar(COLORS.orange.base, 0.3)}`,
            "&:hover": { backgroundColor: alphaVar(COLORS.orange[100], 0.1) },
          }}
        >
          <Add fontSize="small" />
        </Button>
      </Box>
    </Card>
  );
};

export const AreaCard = memo(
  AreaCardBase,
  (prev, next) => {
    const a: any = prev.area;
    const b: any = next.area;

    return (
      a.uid === b.uid &&
      a.squareFootages?.[0]?.quantity === b.squareFootages?.[0]?.quantity &&
      a.tasks?.length === b.tasks?.length &&
      a.tasks?.every((t: any, i: number) => t.active === b.tasks[i].active) &&
      prev.isOtherArea === next.isOtherArea
    );
  }
);